import React from 'react';

import { convertECurrencyTypeToSymbol, ECurrencyType } from '~/src/dash/api/exchange-rates';
import { publicaColors } from '~/src/dash/utils/colors';
import { TKey } from '~/src/dash/publicaUI';

export interface IObjectWithDefault {
  isDefault?: boolean;
}

// add new group bys here
export type TableGroupByKey = 'Advertiser' | 'Bidder' | 'Demand Source';

export type TableGroupByKeyForReportsDashboard =
  | 'Advertiser'
  | 'Order'
  | 'Line Item'
  | 'Ad Unit'
  | 'Size'
  | 'Tile'
  | 'Section'
  | 'Creative'
  | 'Traffic Source';

export interface ITableGroupBy extends IObjectWithDefault {
  // swap the column display label with this
  alias?: string;
  // key found in api response
  apiKey: string;
  // key used for outgoing api call
  matchKey: string;
  // api response key to be used as display label
  displayKey: string;
  // style object for column header text
  headerStyle?: React.CSSProperties;
  // true makes it so that the table sorts only the data stored in state
  // false implies an api call and handle will be fired
  isTableControllingSort?: boolean;
  // adds this to the view by drop down
  isViewBy?: boolean;
  // this must match object key aka BrandsDashboardMetricKey
  label: TableGroupByKey;
  pluralLabel: string;
  // key used for outgoing api call
  requestKey: string;
  tooltipMessage?: string;
  // set the order of the drill path
  drillPath: TableGroupByKey[];
  // subheader for singleSelect
  subheader?: string;
  isSelectable: boolean;
  tKey?: TKey;
}

export interface ITableGroupByForReportsDashboard extends IObjectWithDefault {
  // swap the column display label with this
  alias?: string;
  // key found in api response
  apiKey: string;
  // key used for outgoing api call
  matchKey: string;
  // api response key to be used as display label
  displayKey: string;
  // style object for column header text
  headerStyle?: React.CSSProperties;
  // true makes it so that the table sorts only the data stored in state
  // false implies an api call and handle will be fired
  isTableControllingSort?: boolean;
  // adds this to the view by drop down
  isViewBy?: boolean;
  // this must match object key aka BrandsDashboardMetricKey
  label: TableGroupByKeyForReportsDashboard;
  pluralLabel: string;
  // key used for outgoing api call
  requestKey: string;
  tooltipMessage?: string;
  // set the order of the drill path
  drillPath: TableGroupByKeyForReportsDashboard[];
  // subheader for singleSelect
  subheader?: string;
  isSelectable: boolean;
}

// add new metrics here
export type TableMetricKey =
  | 'CPM'
  | 'CPM Compare'
  | 'Impressions'
  | 'Impressions Compare'
  | 'Total Revenue'
  | 'Total Revenue Compare'
  | 'Total Tracked Ads'
  | 'Rendered Impressions'
  | 'Invalid Traffic Ads'
  | 'Measured Ads'
  | 'Viewable Impressions'
  | 'Blocked Ads'
  | 'Render Rate'
  | 'Invalid Traffic Rate'
  | 'Measured Rate'
  | 'Viewability Rate'
  | 'Blocked Rate'
  | 'GroupM Display Impressions'
  | 'GroupM Video Impressions'
  | 'Total Billable Impressions'
  | 'GroupM Display Viewable Rate'
  | 'GroupM Video Viewable Rate'
  | 'Sitting Duck Bot Impressions'
  | 'Standard Bot Impressions'
  | 'Volunteer Bot Impressions'
  | 'Profile Bot Impressions'
  | 'Masked Bot Impressions'
  | 'Nomadic Bot Impressions'
  | 'Other Bot Impressions'
  | 'Failed Adult Ads'
  | 'Failed Alcohol Ads'
  | 'Failed Hate Speech Ads'
  | 'Failed Illegal Downloads Ads'
  | 'Failed Drugs Ads'
  | 'Failed Offensive Language Ads'
  | 'Failed Violence Ads'
  | 'Refresh Ads'
  | 'IAS Refreshed Ads'
  | 'Refreshed Ad Rate'
  | 'IAS Refreshed Ad Rate'
  | 'Publicis Fully In View Display Impressions'
  | 'Publicis Fully In View Video Impressions'
  | 'Publicis Display Viewable Rate'
  | 'Publicis Video Viewable Rate'
  | 'Blocked by Brand Safety'
  | 'Blocked by Brand Suitability'
  | 'Blocked by Custom Keyword Exclusion List'
  | 'Blocked by Custom URL/App Exclusion List'
  | 'Blocked by Geo'
  | 'Blocked by Invalid Traffic'
  | 'Blocked by Invisible URL'
  | 'Blocked by Target Language Mismatch'
  | 'In View At Page Open Rate'
  | 'In View > 5s Rate'
  | 'In View > 15s Rate'
  | 'Not Enough Time'
  | 'Not Enough Pixels'
  | 'Never On Screen'
  | 'Out Of Focus'
  | 'Video Never Started'
  | 'Other Out Of View'
  | 'Failed for Brand Safety'
  | 'Failed for Brand Suitability'
  | 'Failed for Custom Keyword Exclusion List'
  | 'Failed for Custom URL/App Exclusion List'
  | 'Failed for Geo'
  | 'Failed for Invalid Traffic'
  | 'Failed for Invisible URL'
  | 'Failed for Target Language Mismatch'
  | 'Blocked Adult Ads'
  | 'Blocked Alcohol Ads'
  | 'Blocked Hate Speech Ads'
  | 'Blocked Illegal Downloads Ads'
  | 'Blocked Drugs Ads'
  | 'Blocked Offensive Language Ads'
  | 'Blocked Violence Ads'
  | '% In View'
  | '% Out Of View'
  | '% Ad Never Started'
  | '% Completed'
  | '% Completed In View'
  | '% Muted'
  | '% Full Screen'
  | '% Click Through (CTR)'
  | 'General Invalid Traffic (GIVT) Ads'
  | 'Bots (SIVT) Ads'
  | 'Hidden Ads (SIVT)'
  | 'Domain Spoofing (SIVT)'
  | 'Incentivized Browsing (RVI) Ads'
  | 'Proxy Servers (RVI) Ads'
  | 'Sophisticated Invalid Traffic Ads'
  | 'Reduced Value Inventory Ads'
  | 'General Invalid Traffic Rate'
  | 'Sophisticated Invalid Traffic Rate'
  | 'Reduced Value Inventory Rate';

export interface ITableMetric extends IObjectWithDefault {
  // swap the column display label with this
  alias?: string;
  // key found in api response
  apiKey: string;
  defaultOrderDirection: OrderDirectionType;
  // style object for column header text
  headerStyle?: React.CSSProperties;
  isCompareValue?: boolean;
  // true allows the column header to be clicked on
  isSelectable: boolean;
  // true will let the table handle the sort. no new api call
  isTableControllingSort?: boolean;
  // this must match object key aka BrandsDashboardMetricKey
  label: TableMetricKey;
  // key used for outgoing api call
  requestKey: string;
  valueType: ValueType;
  tooltipMessage?: string;
  subheader?: string;
  tKey?: TKey;
}

export type ITableGroupBys = Record<TableGroupByKey, ITableGroupBy>;
export type ITableGroupBysForReportsDashboard = Record<
  TableGroupByKeyForReportsDashboard,
  ITableGroupByForReportsDashboard
>;
export type ITableMetrics = Record<TableMetricKey, ITableMetric>;

export interface ITableMetricValueTypes {
  [key: string]: {
    valueType: ValueType;
  };
}

// add new value types
export type ValueType =
  | 'integer'
  | 'currency'
  | 'percentChange'
  | 'name'
  | 'currency_rate'
  | 'latency'
  | 'percent'
  | 'whole_number';

export const valueTypes = {
  INTEGER: 'integer' as ValueType,
  CURRENCY: 'currency' as ValueType,
  NAME: 'name' as ValueType,
  PERCENT_CHANGE: 'percentChange' as ValueType,
};

export type OrderDirectionType = 'asc' | 'desc';

export const ORDER_DIRECTION = {
  ASC: 'asc' as OrderDirectionType,
  DESC: 'desc' as OrderDirectionType,
};

export interface ITableCol {
  // see prop notes above
  alias?: string;
  apiKey: string;
  defaultOrderDirection: OrderDirectionType;
  displayKey: string;
  headerStyle?: React.CSSProperties;
  isCompareValue?: boolean;
  isSelectable: boolean;
  isTableControllingSort?: boolean;
  label: TableGroupByKey | TableGroupByKeyForReportsDashboard | TableMetricKey;
  requestKey: string;
  tooltipMessage?: string;
  valueType: ValueType;
  tKey?: TKey;
}

export const formatNumberForDisplay = (
  num: number,
  decimalSpaces: number = 0,
  absoluteValue: boolean = false,
) => {
  const n = absoluteValue ? Math.abs(num) : num;
  const value = decimalSpaces > 0 ? n.toFixed(decimalSpaces) : n;
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const displayArrow = (value: number) => {
  switch (true) {
    case value === 0:
      return '';
    case value > 0:
      return '▲';
    default:
      return '▼';
  }
};

export const styleCellValue = (value: string | number, valueType: ValueType) => {
  const { PERCENT_CHANGE } = valueTypes;
  switch (valueType) {
    case PERCENT_CHANGE:
      const { darkGreen, veryLightGreen, lightRed, veryLightRed, gray, veryLightGray } =
        publicaColors;
      if (value === '') {
        return {
          padding: '4px 6px',
          fontSize: '11px',
          fontWeight: 500,
          borderRadius: '6px',
          backgroundColor: veryLightGray,
          color: gray,
        };
      }
      return {
        padding: '4px 6px',
        fontSize: '11px',
        fontWeight: 500,
        borderRadius: '6px',
        backgroundColor:
          value !== 0 ? (Number(value) > 0 ? veryLightGreen : veryLightRed) : veryLightGray,
        color: value !== 0 ? (Number(value) >= 0 ? darkGreen : lightRed) : gray,
      };
    default:
      return {} as React.CSSProperties;
  }
};

export const formatCellValue = (
  value: any,
  valueType: string,
  currencyType?: ECurrencyType,
): string | any => {
  const { CURRENCY, INTEGER, PERCENT_CHANGE } = valueTypes;
  const absoluteValue = true;
  if (value === undefined || value === '') {
    return 'N/A';
  }
  switch (valueType) {
    case CURRENCY:
      return `${convertECurrencyTypeToSymbol(
        currencyType || ECurrencyType.CURRENCY_TYPE_USD,
      )} ${formatNumberForDisplay(value, 2)}`;
    case INTEGER:
      return formatNumberForDisplay(value);
    case PERCENT_CHANGE:
      return `${displayArrow(value)} ${formatNumberForDisplay(value, 1, absoluteValue)} %`;
    default:
      return value;
  }
};
